import {
  PermissionAccessLevel,
  type PermissionResourceType,
} from '@ardoq/api-types';
import { t, type I18nLocale } from '@ardoq/i18n';

/* ----------------------------------------------------------------------
  -----------------------------------------------------------------------
  ---------------------                         -------------------------
  ---------------------          TYPES          -------------------------
  ---------------------                         -------------------------
  -----------------------------------------------------------------------
  ----------------------------------------------------------------------*/

type WithFallback<T> = T & {
  fallback: T extends { [k in keyof T]: infer R } ? R : never;
};

export enum PermissionLabelContext {
  REQUEST_ACCESS_DOQY = 'REQUEST_ACCESS_DOQY',
  APPROVAL_DIALOG = 'APPROVAL_DIALOG',
}

export type PermissionAccessLevelLabelMap = {
  displayLabel: string;
  displayPhrase: string;
  description?: string;
};

type PermissionLabelByContextMap = WithFallback<{
  [key in PermissionLabelContext]?: WithFallback<
    Record<PermissionAccessLevel, PermissionAccessLevelLabelMap>
  >;
}>;

type PermissionLabelDescriptor = WithFallback<{
  [key in PermissionResourceType]?: PermissionLabelByContextMap;
}>;

/* ----------------------------------------------------------------------
  -----------------------------------------------------------------------
  ---------------------                         -------------------------
  ---------------------        CONSTANTS        -------------------------
  ---------------------                         -------------------------
  -----------------------------------------------------------------------
  ----------------------------------------------------------------------*/

const DEFAULT_FALLBACK_CONTEXT = {
  [PermissionAccessLevel.ADMIN]: {
    displayLabel: 'admin',
    displayPhrase: 'admin permissions',
  },
  [PermissionAccessLevel.EDIT]: {
    displayLabel: 'edit',
    displayPhrase: 'edit permissions',
  },
  [PermissionAccessLevel.READ]: {
    displayLabel: 'read',
    displayPhrase: 'read permissions',
  },
  fallback: { displayLabel: 'no access', displayPhrase: 'no access' },
} as const;

const PERMISSION_LABEL_DESCRIPTOR: PermissionLabelDescriptor = {
  fallback: {
    fallback: DEFAULT_FALLBACK_CONTEXT,
  },
  report: {
    fallback: {
      ...DEFAULT_FALLBACK_CONTEXT,
      [PermissionAccessLevel.READ]: {
        displayLabel: 'view',
        displayPhrase: 'to view',
      },
    },
    [PermissionLabelContext.REQUEST_ACCESS_DOQY]: {
      ...DEFAULT_FALLBACK_CONTEXT,
      [PermissionAccessLevel.READ]: {
        displayLabel: 'view',
        displayPhrase: 'to view',
      },
    },
    [PermissionLabelContext.APPROVAL_DIALOG]: {
      ...DEFAULT_FALLBACK_CONTEXT,
      [PermissionAccessLevel.READ]: {
        ...DEFAULT_FALLBACK_CONTEXT[PermissionAccessLevel.READ],
        displayPhrase: 'read-only permissions',
        description:
          'view the report, but not edit its configuration or view its underlying workspaces',
      },
    },
  },
  dashboard: {
    fallback: {
      ...DEFAULT_FALLBACK_CONTEXT,
      [PermissionAccessLevel.READ]: {
        displayLabel: 'view',
        displayPhrase: 'to view',
      },
    },
    [PermissionLabelContext.REQUEST_ACCESS_DOQY]: {
      ...DEFAULT_FALLBACK_CONTEXT,
      [PermissionAccessLevel.READ]: {
        displayLabel: 'view',
        displayPhrase: 'to view',
      },
    },
    [PermissionLabelContext.APPROVAL_DIALOG]: {
      ...DEFAULT_FALLBACK_CONTEXT,
      [PermissionAccessLevel.READ]: {
        ...DEFAULT_FALLBACK_CONTEXT[PermissionAccessLevel.READ],
        displayPhrase: 'read-only permissions',
        description:
          'view the entire dashboard and drill-down into certain widgets depending on their access to underlying reports',
      },
    },
  },
  survey: {
    fallback: {
      ...DEFAULT_FALLBACK_CONTEXT,
      [PermissionAccessLevel.READ]: {
        displayLabel: 'contribute',
        displayPhrase: 'to contribute to',
      },
    },
    [PermissionLabelContext.REQUEST_ACCESS_DOQY]: {
      ...DEFAULT_FALLBACK_CONTEXT,
      [PermissionAccessLevel.READ]: {
        displayLabel: 'submit survey answers',
        displayPhrase: 'to contribute to',
      },
    },
    [PermissionLabelContext.APPROVAL_DIALOG]: {
      ...DEFAULT_FALLBACK_CONTEXT,
      [PermissionAccessLevel.READ]: {
        displayLabel: 'submit survey answers',
        displayPhrase: 'the ability to submit survey answers',
        description:
          'answer the survey directly, but not view or edit the survey configuration itself',
      },
    },
  },
};

/* ----------------------------------------------------------------------
  -----------------------------------------------------------------------
  ----------------                                  ---------------------
  ----------------    Translation key composition   ---------------------
  ----------------                                  ---------------------
  -----------------------------------------------------------------------
  ----------------------------------------------------------------------*/

type ResourceTypeKeyToken = PermissionResourceType | 'fallback';
type ContextKeyToken = PermissionLabelContext | 'fallback';
type AccessLevelKeyToken = PermissionAccessLevel | 'fallback';

const CONTEXT_KEY_TOKEN: Record<PermissionLabelContext, string> = {
  [PermissionLabelContext.REQUEST_ACCESS_DOQY]: 'requestAccessDoqy',
  [PermissionLabelContext.APPROVAL_DIALOG]: 'approvalDialog',
};

const ACCESS_LEVEL_KEY_TOKEN: Record<PermissionAccessLevel, string> = {
  [PermissionAccessLevel.ADMIN]: 'admin',
  [PermissionAccessLevel.EDIT]: 'edit',
  [PermissionAccessLevel.READ]: 'read',
};

const toContextKeyToken = (context: ContextKeyToken): string =>
  context === 'fallback' ? 'fallback' : CONTEXT_KEY_TOKEN[context];

const toAccessLevelKeyToken = (level: AccessLevelKeyToken): string =>
  level === 'fallback' ? 'noAccess' : ACCESS_LEVEL_KEY_TOKEN[level];

const buildLabelKey = (
  resourceType: ResourceTypeKeyToken,
  context: ContextKeyToken,
  level: AccessLevelKeyToken,
  field: 'displayLabel' | 'displayPhrase' | 'description'
): string =>
  `accessControl:permission.${resourceType}.${toContextKeyToken(context)}.${toAccessLevelKeyToken(level)}.${field}`;

/* ----------------------------------------------------------------------
  -----------------------------------------------------------------------
  ----------------                                  ---------------------
  ----------------  Compile Into ResourceTypes Map  ---------------------
  ----------------                                  ---------------------
  -----------------------------------------------------------------------
  ----------------------------------------------------------------------*/

type GetPermissionLabelArgs = {
  permission: PermissionAccessLevel | null;
  resourceType: PermissionResourceType;
  context?: PermissionLabelContext;
  i18nLocale: I18nLocale;
};

const resolveDescriptorEntry = (
  permission: PermissionAccessLevel | null,
  resourceType: PermissionResourceType,
  context: PermissionLabelContext | undefined
): {
  resolvedResourceType: ResourceTypeKeyToken;
  resolvedContext: ContextKeyToken;
  resolvedLevel: AccessLevelKeyToken;
  englishEntry: PermissionAccessLevelLabelMap;
} => {
  const resourceTypeEntry = PERMISSION_LABEL_DESCRIPTOR[resourceType];
  const permissionsByResourceType =
    resourceTypeEntry ?? PERMISSION_LABEL_DESCRIPTOR.fallback;
  const resolvedResourceType: ResourceTypeKeyToken =
    resourceTypeEntry !== undefined ? resourceType : 'fallback';

  const contextEntry =
    context !== undefined ? permissionsByResourceType[context] : undefined;
  const permissionLabelByContext =
    contextEntry ?? permissionsByResourceType.fallback;
  const resolvedContext: ContextKeyToken =
    context !== undefined && contextEntry !== undefined ? context : 'fallback';

  const levelKey: AccessLevelKeyToken = permission ?? 'fallback';
  const levelEntry = permissionLabelByContext[levelKey];
  const englishEntry = levelEntry ?? permissionLabelByContext.fallback;
  const resolvedLevel: AccessLevelKeyToken =
    levelEntry !== undefined ? levelKey : 'fallback';

  return {
    resolvedResourceType,
    resolvedContext,
    resolvedLevel,
    englishEntry,
  };
};

/**
 * Returns the localized label for a given permission and resource type.
 *
 * The English defaults are resolved from `PERMISSION_LABEL_DESCRIPTOR` and
 * passed as fallbacks to `t()`, so callers always receive a non-empty string
 * for `displayLabel`/`displayPhrase` and an optional `description`.
 */
export const getPermissionLabel = ({
  permission,
  resourceType,
  context,
  i18nLocale,
}: GetPermissionLabelArgs): PermissionAccessLevelLabelMap => {
  const { resolvedResourceType, resolvedContext, resolvedLevel, englishEntry } =
    resolveDescriptorEntry(permission, resourceType, context);

  const displayLabel = t(
    i18nLocale,
    buildLabelKey(
      resolvedResourceType,
      resolvedContext,
      resolvedLevel,
      'displayLabel'
    ),
    englishEntry.displayLabel
  );
  const displayPhrase = t(
    i18nLocale,
    buildLabelKey(
      resolvedResourceType,
      resolvedContext,
      resolvedLevel,
      'displayPhrase'
    ),
    englishEntry.displayPhrase
  );
  if (englishEntry.description === undefined) {
    return { displayLabel, displayPhrase };
  }
  return {
    displayLabel,
    displayPhrase,
    description: t(
      i18nLocale,
      buildLabelKey(
        resolvedResourceType,
        resolvedContext,
        resolvedLevel,
        'description'
      ),
      englishEntry.description
    ),
  };
};
